import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminDashboard as AdminDashboardComponent, AdminAccountDialog, SupportFeedbackDialog, PatientAssignmentInterface, type Patient, Loader } from '@joinomu/ui'
import { useAuth } from '@/hooks/useAuth'
import { authService, emailService } from '@joinomu/shared'
import { supabase } from '@joinomu/shared'
import type { Admin } from '@joinomu/shared'

export function AdminDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [adminData, setAdminData] = useState<Admin | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeItem, setActiveItem] = useState('Dashboard')
  const [patients, setPatients] = useState<any[]>([])
  const [providers, setProviders] = useState<any[]>([])
  const [assignmentLoading, setAssignmentLoading] = useState(false)
  const [accountDialogOpen, setAccountDialogOpen] = useState(false)
  
  // Support/Feedback dialog state
  const [supportFeedbackDialogOpen, setSupportFeedbackDialogOpen] = useState(false)
  const [supportFeedbackType, setSupportFeedbackType] = useState<'support' | 'feedback'>('support')

  useEffect(() => {
    const fetchAdminData = async () => {
      if (user?.id) {
        console.log('🔍 AdminDashboard: Fetching real admin profile for user:', user.id)
        
        try {
          // Fetch real admin profile from profiles table
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, email, role, first_name, last_name, created_at, updated_at')
            .eq('id', user.id)
            .eq('role', 'admin')
            .single()

          if (profileError || !profileData) {
            console.log('❌ AdminDashboard: User is not an admin or profile not found:', profileError)
            setAdminData(null) // Will show access denied
            setLoading(false)
            return
          }

          console.log('✅ AdminDashboard: Valid admin profile found:', profileData)
          
          // Convert profile to admin data format
          const realAdminData = {
            id: profileData.id,
            user_id: profileData.id,
            email: profileData.email,
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            role: 'admin' as const,
            permissions: ['messages', 'patients', 'dashboard'],
            active: true,
            created_at: profileData.created_at,
            updated_at: profileData.updated_at
          }
          
          setAdminData(realAdminData)
          
          // Fetch patients and providers for assignment interface
          await fetchPatientsAndProviders()
          
        } catch (error) {
          console.error('❌ AdminDashboard: Error fetching admin data:', error)
          setAdminData(null)
        }
      }
      setLoading(false)
    }

    fetchAdminData()
  }, [user, navigate])

  const fetchPatientsAndProviders = async () => {
    try {
      console.log('🔍 AdminDashboard: Fetching patients and providers with admin session...')
      
      // Fetch all patients (RLS should allow this for authenticated admin users)
      const { data: patientsData, error: patientsError } = await supabase
        .from('patients')
        .select('id, first_name, last_name, email, profile_id, created_at')
        .order('first_name')
      
      console.log('🔍 AdminDashboard: Patients query result:', { patientsData, patientsError })
      
      if (patientsError) {
        console.error('❌ AdminDashboard: Patients query failed:', patientsError)
        // Don't fall back to mock data - show the actual error
        setPatients([])
      } else {
        console.log('✅ AdminDashboard: Found patients:', patientsData?.length || 0)
        setPatients(patientsData || [])
      }

      // Fetch all providers with profile data
      const { data: providersData, error: providersError } = await supabase
        .from('providers')
        .select(`
          id,
          specialty,
          active,
          profiles (
            first_name,
            last_name,
            email
          )
        `)
        .eq('active', true)
        .order('id')
      
      if (providersError) {
        console.error('❌ AdminDashboard: Providers query failed:', providersError)
        setProviders([])
      } else {
        console.log('✅ AdminDashboard: Found providers:', providersData?.length || 0)
        
        // Transform provider data to flatten profile information
        const transformedProviders = (providersData || []).map(provider => ({
          id: provider.id,
          specialty: provider.specialty,
          active: provider.active,
          first_name: provider.profiles?.[0]?.first_name || '',
          last_name: provider.profiles?.[0]?.last_name || '',
          email: provider.profiles?.[0]?.email || ''
        }))
        
        setProviders(transformedProviders)
      }
      
    } catch (error) {
      console.error('Error fetching patients and providers:', error)
      setPatients([])
      setProviders([])
    }
  }

  const handleAssignPatient = async (
    patientId: string,
    providerId: string,
    treatmentType: string,
    isPrimary: boolean
  ) => {
    setAssignmentLoading(true)
    try {
      // TODO: Implement assignPatientToProvider method in authService
      const result = { success: false, error: 'Method not implemented' }
      console.log('TODO: Assign patient', patientId, 'to provider', providerId, 'with treatment', treatmentType, 'primary:', isPrimary)
      return result
    } catch (error) {
      return { success: false, error: 'Assignment failed' }
    } finally {
      setAssignmentLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      console.log('🔄 Admin dashboard: Starting sign out...')
      await authService.signOut()
      console.log('✅ Admin dashboard: Sign out successful, navigating to home')
      navigate('/')
    } catch (error) {
      console.error('❌ Admin dashboard: Error signing out:', error)
      // Force navigation even if sign out fails
      navigate('/')
    }
  }

  const handleNavigate = (item: string) => {
    if (item === 'Patients') {
      // Navigate to dedicated patients page instead of showing inline table
      navigate('/admin/patients')
      return
    }
    if (item === 'Providers') {
      // Navigate to dedicated providers page
      navigate('/admin/providers')
      return
    }
    setActiveItem(item)
  }

  const handleAccountClick = () => {
    setAccountDialogOpen(true)
  }

  const handleSupportFeedbackClick = (type: "Support" | "Feedback") => {
    console.log('🎯 PAGES Admin handleSupportFeedbackClick called with type:', type)
    console.log('🎯 PAGES Admin current supportFeedbackDialogOpen state:', supportFeedbackDialogOpen)
    console.log('🎯 PAGES Admin current supportFeedbackType:', supportFeedbackType)
    setSupportFeedbackType(type.toLowerCase() as 'support' | 'feedback')
    setSupportFeedbackDialogOpen(true)
    console.log('🎯 PAGES Admin set supportFeedbackDialogOpen to true')
    console.log('🎯 PAGES Admin function execution complete')
  }

  const handleSupportFeedbackSubmit = async (message: string, type: 'support' | 'feedback') => {
    if (!user?.email) {
      return { 
        success: false, 
        error: 'User email not available' 
      }
    }

    try {
      const result = await emailService.sendSupportFeedback({
        type,
        message,
        userEmail: user.email,
        userName: adminData?.first_name || user.email.split('@')[0] || 'Anonymous Admin'
      })

      return result
    } catch (error) {
      console.error('Failed to send support/feedback message:', error)
      return {
        success: false,
        error: 'Failed to send message'
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader />
      </div>
    )
  }

  if (!adminData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin privileges.</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white rounded">Return to Home</button>
        </div>
      </div>
    )
  }

  const userData = {
    name: `${adminData.first_name} ${adminData.last_name}`,
    email: adminData.email,
    avatar: ''
  }

  if (activeItem === 'Assignments') {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <button 
              onClick={() => setActiveItem('Dashboard')} 
              className="text-blue-600 hover:underline mb-4"
            >
              ← Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold">Patient Assignments</h1>
          </div>
          <PatientAssignmentInterface
            patients={patients}
            providers={providers}
            onAssignPatient={handleAssignPatient}
            loading={assignmentLoading}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <AdminDashboardComponent
        user={userData}
        onLogout={handleSignOut}
        activeItem={activeItem}
        showPatientTable={activeItem === 'Patients'}
        onNavigate={handleNavigate}
        onAccountClick={handleAccountClick}
        onSupportFeedbackClick={handleSupportFeedbackClick}
        patients={patients.map(patient => ({
          id: patient.id,
          profile_id: patient.profile_id,
          name: `${patient.first_name} ${patient.last_name}`,
          email: patient.email,
          careTeam: [],
          treatments: [],
          medications: [],
          status: ['Active'],
          hasCompletedIntake: true
        } as Patient))}
      />
      
      <AdminAccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
      />
      
      <SupportFeedbackDialog
        open={supportFeedbackDialogOpen}
        onOpenChange={setSupportFeedbackDialogOpen}
        type={supportFeedbackType}
        onSubmit={handleSupportFeedbackSubmit}
      />
    </>
  )
}